import { ApiError, asyncJsonParse, isRecord } from "@tsmono/util";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export const VIEW_REQUEST_HEADER = "X-Inspect-View-Request";
export const VIEW_REQUEST_HEADER_VALUE = "true";

/**
 * Unwrap a FastAPI `HTTPException` body — wire-encoded as
 * `{"detail": "..."}` — into the bare detail string.
 *
 * Returns the input unchanged when:
 *   - the body isn't valid JSON (e.g. the server sent plain text), or
 *   - the JSON has no top-level `detail` string (older endpoints,
 *     custom error shapes).
 *
 * Used by callers that build their own `ApiError` from a non-OK
 * response so the dialog renders e.g. `Empty tag is not allowed`
 * instead of `{"detail": "Empty tag is not allowed"}`.
 */
export function unwrapFastapiDetail(body: string): string {
  if (!body) return body;
  try {
    const parsed: unknown = JSON.parse(body);
    if (isRecord(parsed) && typeof parsed["detail"] === "string") {
      return parsed["detail"];
    }
  } catch {
    // Not JSON — fall through.
  }
  return body;
}

export interface Request<T> {
  headers?: Record<string, string>;
  body?: string;
  parse?: (text: string) => Promise<T>;
  handleError?: (status: number) => T | undefined;
}

export type HeaderProvider = () => Promise<Record<string, string>>;

export interface ServerRequestApi {
  fetchString: (
    method: HttpMethod,
    path: string,
    headers?: Record<string, string>,
    body?: string
  ) => Promise<{
    parsed: unknown;
    raw: string;
  }>;
  fetchBytes: (method: HttpMethod, path: string) => Promise<Uint8Array>;
  fetchType: <T>(
    method: HttpMethod,
    path: string,
    request: Request<T>
  ) => Promise<{ raw: string; parsed: T }>;
}

export function serverRequestApi(
  baseUrl?: string,
  getHeaders?: HeaderProvider,
  customFetch?: typeof fetch
): ServerRequestApi {
  const fetchFn = customFetch ?? fetch;
  const apiUrl = baseUrl || "";

  function addViewRequestHeader(
    method: HttpMethod,
    headers: Record<string, string>
  ): void {
    if (method !== "GET") {
      headers[VIEW_REQUEST_HEADER] = VIEW_REQUEST_HEADER_VALUE;
    }
  }

  function buildApiUrl(path: string): string {
    if (!apiUrl) {
      return path;
    }
    const base = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return base + cleanPath;
  }

  function isApiCrossOrigin(): boolean {
    try {
      return Boolean(
        apiUrl && new URL(apiUrl).origin !== window.location.origin
      );
    } catch {
      return false;
    }
  }

  const fetchType = async <T>(
    method: HttpMethod,
    path: string,
    request: Request<T>
  ): Promise<{ raw: string; parsed: T }> => {
    const url = buildApiUrl(path);

    const responseHeaders: Record<string, string> = {
      Accept: "application/json",
      Pragma: "no-cache",
      Expires: "0",
      "Cache-Control": "no-cache",
      ...request.headers,
    };

    if (getHeaders) {
      const globalHeaders = await getHeaders();
      Object.assign(responseHeaders, globalHeaders);
    }

    if (request.body) {
      responseHeaders["Content-Type"] = "application/json";
    }
    addViewRequestHeader(method, responseHeaders);

    const response = await fetchFn(url, {
      method,
      headers: responseHeaders,
      body: request.body,
      credentials: isApiCrossOrigin() ? "include" : "same-origin",
    });

    if (!response.ok) {
      const errorResponse = request.handleError?.(response.status);
      if (errorResponse) {
        return {
          raw: response.statusText,
          parsed: errorResponse,
        };
      }

      const message = (await response.text()) || response.statusText;
      throw new ApiError(
        response.status,
        `API Error ${response.status}: ${message}`
      );
    }

    const text = await response.text();
    const parse = request.parse || asyncJsonParse;
    return {
      parsed: (await parse(text)) as T,
      raw: text,
    };
  };

  const fetchString = async (
    method: HttpMethod,
    path: string,
    headers?: Record<string, string>,
    body?: string
  ): Promise<{ parsed: unknown; raw: string }> => {
    const url = buildApiUrl(path);

    const requestHeaders: Record<string, string> = {
      Accept: "application/json",
      Pragma: "no-cache",
      Expires: "0",
      "Cache-Control": "no-cache",
      ...headers,
    };

    if (getHeaders) {
      const globalHeaders = await getHeaders();
      Object.assign(requestHeaders, globalHeaders);
    }

    if (body) {
      requestHeaders["Content-Type"] = "application/json";
    }
    addViewRequestHeader(method, requestHeaders);

    const response = await fetchFn(url, {
      method,
      headers: requestHeaders,
      body,
      credentials: isApiCrossOrigin() ? "include" : "same-origin",
    });

    if (response.ok) {
      const text = await response.text();
      return {
        parsed: await asyncJsonParse(text),
        raw: text,
      };
    }

    const message = (await response.text()) || response.statusText;
    throw new ApiError(response.status, `HTTP ${response.status}: ${message}`);
  };

  const fetchBytes = async (
    method: HttpMethod,
    path: string
  ): Promise<Uint8Array> => {
    const url = buildApiUrl(path);

    const headers: Record<string, string> = {
      Accept: "application/octet-stream",
      Pragma: "no-cache",
      Expires: "0",
      "Cache-Control": "no-cache",
    };

    if (getHeaders) {
      const globalHeaders = await getHeaders();
      Object.assign(headers, globalHeaders);
    }
    addViewRequestHeader(method, headers);

    const response = await fetchFn(url, {
      method,
      headers,
      credentials: isApiCrossOrigin() ? "include" : "same-origin",
    });

    if (!response.ok) {
      const message = (await response.text()) || response.statusText;
      throw new ApiError(
        response.status,
        `HTTP ${response.status}: ${message}`
      );
    }

    const buffer = await response.arrayBuffer();
    return new Uint8Array(buffer);
  };

  return {
    fetchString,
    fetchBytes,
    fetchType,
  };
}
